import { MINTABLE_TOKENS, isOneTokenV1Contract } from 'constants/contracts';
import { IchiInsights } from 'common/models/Insights';
import { Nullable } from 'common/models/optional';
import { ChainId } from 'constants/constants';
import isError from 'lodash/isError';
import numbro from 'numbro';
import { CryptoUtils, GAS_SETTINGS } from 'utils/cryptoUtils';
import { isUserRejectedError, isViemBaseError } from 'utils/errorUtils';
import { NumberUtils } from 'utils/numberUtils';
import { StorageUtils } from 'utils/storageUtils';
import { MintEvent } from 'xstate/mintEvent';
import { MintApiUtils } from './mintApiUtils';
import { readContract, writeContract } from '@wagmi/core';
import { Address, BaseError, UserRejectedRequestError } from 'viem';
import { commonOracle } from 'constants/abis/commonOracle';
import { erc20 } from 'constants/abis/erc20';
import { oneTokenV1 } from 'constants/abis/oneTokenV1';

export namespace MintApi {
  export type MintResponse = {
    message: Nullable<string>;
  };

  export type GetMintDataResponse = {
    memberTokenString: string;
    memberTokenBN: bigint;
    collateralTokenBN: bigint;
    isSufficientCollateral: boolean;
    isSufficientMemberToken: boolean;
    isMemberApproved: boolean;
    isCollateralApproved: boolean;
    message?: string;
  };

  type MintArgs = {
    chainId: ChainId;
    amount?: string;
    mintingRatio?: number;
    mintingFee?: number;
    accountAddress: Address;
    tokenToMint: Nullable<Address>;
  };

  export interface IMint {
    mint(args: MintArgs): Promise<Nullable<StorageUtils.StoredTx>>;
    getMintData(args: MintArgs): Promise<GetMintDataResponse>;
    resetMemberAllowance(
      args: MintArgs
    ): Promise<Nullable<StorageUtils.StoredTx>>;
    resetCollateralAllowance(
      args: MintArgs
    ): Promise<Nullable<StorageUtils.StoredTx>>;
    approveMemberAllowance(
      args: MintArgs
    ): Promise<Nullable<StorageUtils.StoredTx>>;
    approveCollateralAllowance(
      args: MintArgs
    ): Promise<Nullable<StorageUtils.StoredTx>>;
  }

  export class OneTokenMint implements IMint {
    private static instance: IMint;

    static maxApproval = 2n ** 256n - 1n;
    static maxApprovalString = OneTokenMint.maxApproval.toString();

    // These will primarily be used for local storage lookups for determining if there
    // are in-flight transactions
    static mintFN = 'mint';
    static resetMemberAllowanceFN = 'resetMemberAllowance';
    static approveMemberAllowanceFN = 'approveMemberAllowance';
    static resetCollateralAllowanceFN = 'resetCollateralAllowance';
    static approveCollateralAllowanceFN = 'approveCollateralAllowance';

    private constructor() {}

    public static getInstance(): IMint {
      if (!OneTokenMint.instance) {
        OneTokenMint.instance = new OneTokenMint();
      }
      return OneTokenMint.instance;
    }

    async mint(args: MintArgs): Promise<Nullable<StorageUtils.StoredTx>> {
      let storedTx: Nullable<StorageUtils.StoredTx> = null;
      const functionName = MintApi.OneTokenMint.mintFN;

      if (!args.tokenToMint) {
        console.warn('Cannot get mint related data without a token to mint');
        const message = 'No token to mint address provided';
        const error = new Error(message);
        IchiInsights.trackException({
          error,
          properties: {
            ...args,
            handledAt: 'mintApi:mint',
          },
        });
        throw error;
      }

      const tokenInfo = MINTABLE_TOKENS[args.chainId][args.tokenToMint];

      if (tokenInfo == null) {
        const message = `Unable to mint ${args.amount} Could not find contract with address: ${args.tokenToMint}`;
        const error = new Error(message);
        IchiInsights.trackException({
          error,
          properties: {
            ...args,
            handledAt: `mintApi:${OneTokenMint.mintFN}`,
          },
        });
        throw new Error(message);
      }

      if (
        isOneTokenV1Contract(tokenInfo.token, {
          ...args,
          contractContainer: tokenInfo,
        }) &&
        tokenInfo.collateralToken &&
        args.amount
      ) {
        try {
          const prettyAmount = numbro(args.amount).format({
            thousandSeparated: true,
          });
          const labels: StorageUtils.LabelsArg = {
            labels: {
              // This is showing LP not actual usd value
              executing: `Minting $${prettyAmount}...`,
              success: `Successfully Minted $${prettyAmount}!`,
              error: `Error Minting ${prettyAmount}!`,
            },
          };

          // Otherwise there is no tx in flight, execute contract call
          const stringBN = NumberUtils.stringToBN(args.amount, 18n);

          const tx = await writeContract({
            address: tokenInfo.token.address,
            abi: oneTokenV1,
            functionName: 'mint',
            args: [tokenInfo.collateralToken.address, stringBN],
            gas: CryptoUtils.calculateGasMargin(
              BigInt(GAS_SETTINGS.mint),
              args.chainId
            ),
            account: args.accountAddress,
          });

          storedTx = await StorageUtils.storeTx({
            ...args,
            hash: tx.hash,
            functionName,
            labels: labels.labels,
            value: args.amount,
          });

          IchiInsights.trackEvent({
            event: MintEvent.MINT,
            properties: {
              ...args,
              amountBNString: stringBN,
              handledAt: `mintApi:${OneTokenMint.mintFN}`,
              txHash: tx.hash,
            },
          });
        } catch (err) {
          console.error(err);
          if (isUserRejectedError(err as BaseError)) {
            throw new Error((err as UserRejectedRequestError).shortMessage);
          }
          const e = isViemBaseError(err as BaseError)
            ? new Error((err as BaseError).shortMessage)
            : err;

          const message = `Unable to mint ${args.amount} ${tokenInfo.token.symbol}`;
          IchiInsights.trackException({
            error: e as Error,
            properties: {
              ...args,
              handledAt: `mintApi:${OneTokenMint.mintFN}`,
            },
          });
          throw new Error(message);
        }
      } else {
        const message = `Could not mint: ${tokenInfo.token.symbol}, no contract specified. Try reconnecting your wallet.`;
        const error = new Error(message);
        IchiInsights.trackException({
          error,
          properties: {
            ...args,
            handledAt: `mintApi:${OneTokenMint.mintFN}`,
          },
        });
        throw error;
      }
      return storedTx;
    }

    // This function represents a blocking contract tx that supports local storage.  It will
    // block when called waiting for the tx to complete, however it still also supports local storage
    async resetMemberAllowance(
      args: MintArgs
    ): Promise<Nullable<StorageUtils.StoredTx>> {
      let storedTx: Nullable<StorageUtils.StoredTx> = null;

      if (!args.tokenToMint) {
        console.warn('Cannot get mint related data without a token to mint');
        const message = 'No token to mint address provided';
        const error = new Error(message);
        IchiInsights.trackException({
          error,
          properties: {
            ...args,
            handledAt: 'mintApi:resetMemberAllowance',
          },
        });
        throw error;
      }

      const tokenInfo = MINTABLE_TOKENS[args.chainId][args.tokenToMint];

      if (tokenInfo == null) {
        const message = `Unable to mint ${args.amount} Could not find contract with address: ${args.tokenToMint}`;
        const error = new Error(message);
        IchiInsights.trackException({
          error,
          properties: {
            ...args,
            handledAt: `mintApi:${OneTokenMint.mintFN}`,
          },
        });
        throw new Error(message);
      }

      if (!tokenInfo.memberToken) {
        const message = 'No member contract defined!';
        const error = new Error(message);
        IchiInsights.trackException({
          error,
          properties: {
            ...args,
            handledAt: `mintApi:${OneTokenMint.resetMemberAllowanceFN}`,
          },
        });
        throw error;
      }

      const functionName = OneTokenMint.resetMemberAllowanceFN;

      try {
        const allowance = await readContract({
          address: tokenInfo.memberToken.address,
          abi: erc20,
          functionName: 'allowance',
          args: [args.accountAddress, tokenInfo.token.address],
          chainId: args.chainId,
        });

        const tx = await writeContract({
          address: tokenInfo.memberToken.address,
          abi: erc20,
          functionName: 'decreaseAllowance',
          args: [tokenInfo.token.address, allowance || 0n],
        });

        if (tx) {
          storedTx = await StorageUtils.storeTx({
            ...args,
            hash: tx.hash,
            functionName,
            labels: {
              executing: 'Resetting Member Allowance...',
              success: 'Successfully Reset!',
              error: 'Error Resetting!',
            },
            value: args.amount,
          });
        }
      } catch (err) {
        console.error(err);
        if (isUserRejectedError(err as BaseError)) {
          throw new Error((err as UserRejectedRequestError).shortMessage);
        }
        const error = isViemBaseError(err as BaseError)
          ? new Error((err as BaseError).shortMessage)
          : err;

        IchiInsights.trackException({
          error: error as Error,
          properties: {
            ...args,
            handledAt: `mintApi:${OneTokenMint.resetMemberAllowanceFN}`,
          },
        });
        throw error;
      }
      return storedTx;
    }

    // v1 was approveStimulus
    async approveMemberAllowance(
      args: MintArgs
    ): Promise<Nullable<StorageUtils.StoredTx>> {
      let storedTx: Nullable<StorageUtils.StoredTx> = null;

      if (!args.tokenToMint) {
        console.warn('Cannot get mint related data without a token to mint');
        const message = 'No token to mint address provided';
        const error = new Error(message);
        IchiInsights.trackException({
          error,
          properties: {
            ...args,
            handledAt: 'mintApi:approveMemberAllowance',
          },
        });
        throw error;
      }

      const tokenInfo = MINTABLE_TOKENS[args.chainId][args.tokenToMint];

      if (tokenInfo == null) {
        const message = `Unable to mint ${args.amount} Could not find contract with address: ${args.tokenToMint}`;
        const error = new Error(message);
        IchiInsights.trackException({
          error,
          properties: {
            ...args,
            handledAt: `mintApi:${OneTokenMint.mintFN}`,
          },
        });
        throw new Error(message);
      }

      if (!tokenInfo.memberToken) {
        const message = 'No member contract defined!';
        const error = new Error(message);
        IchiInsights.trackException({
          error,
          properties: {
            ...args,
            handledAt: `mintApi:${OneTokenMint.approveMemberAllowanceFN}`,
          },
        });
        throw error;
      }

      const functionName = OneTokenMint.approveMemberAllowanceFN;

      try {
        const tx = await writeContract({
          address: tokenInfo.memberToken.address,
          abi: erc20,
          functionName: 'approve',
          args: [tokenInfo.token.address, OneTokenMint.maxApproval],
          gas: CryptoUtils.calculateGasMargin(
            BigInt(GAS_SETTINGS.approve),
            args.chainId
          ),
        });

        // Storage the tx hash in local storage
        if (tx) {
          storedTx = await StorageUtils.storeTx({
            hash: tx.hash,
            functionName,
            labels: {
              executing: 'Approving...',
              success: 'Success!',
              error: 'Error!',
            },
            value: args.amount,
            ...args,
          });

          IchiInsights.trackEvent({
            event: MintEvent.APPROVE_MEMBER_ALLOWANCE,
            properties: {
              ...args,
              amount: OneTokenMint.maxApprovalString,
              handledAt: `mintApi:${OneTokenMint.mintFN}`,
              txHash: tx.hash,
            },
          });
        }
      } catch (err) {
        console.error(err);
        if (isUserRejectedError(err as BaseError)) {
          throw new Error((err as UserRejectedRequestError).shortMessage);
        }
        const error = isViemBaseError(err as BaseError)
          ? new Error((err as BaseError).shortMessage)
          : err;

        console.error(error);
        IchiInsights.trackException({
          error: error as Error,
          properties: {
            ...args,
            handledAt: `mintApi:${OneTokenMint.approveMemberAllowanceFN}`,
          },
        });
      }
      return storedTx;
    }

    async resetCollateralAllowance(
      args: MintArgs
    ): Promise<Nullable<StorageUtils.StoredTx>> {
      let storedTx: Nullable<StorageUtils.StoredTx> = null;

      if (!args.tokenToMint) {
        console.warn('Cannot get mint related data without a token to mint');
        const message = 'No token to mint address provided';
        const error = new Error(message);
        IchiInsights.trackException({
          error,
          properties: {
            ...args,
            handledAt: 'mintApi:resetCollateralAllowance',
          },
        });
        throw error;
      }

      const tokenInfo = MINTABLE_TOKENS[args.chainId][args.tokenToMint];

      if (tokenInfo == null) {
        const message = `Unable to mint ${args.amount} Could not find contract with address: ${args.tokenToMint}`;
        const error = new Error(message);
        IchiInsights.trackException({
          error,
          properties: {
            ...args,
            handledAt: `mintApi:${OneTokenMint.mintFN}`,
          },
        });
        throw new Error(message);
      }

      if (!tokenInfo.collateralToken) {
        const message = 'No collateral contract defined!';
        const error = new Error(message);
        IchiInsights.trackException({
          error,
          properties: {
            ...args,
            handledAt: `mintApi:${OneTokenMint.resetCollateralAllowanceFN}`,
          },
        });
        throw error;
      }

      const functionName = OneTokenMint.resetCollateralAllowanceFN;

      try {
        const allowance = await readContract({
          address: tokenInfo.collateralToken.address,
          abi: erc20,
          functionName: 'allowance',
          args: [args.accountAddress, tokenInfo.token.address],
          chainId: args.chainId,
        });

        const tx = await writeContract({
          address: tokenInfo.collateralToken.address,
          abi: erc20,
          functionName: 'decreaseAllowance',
          args: [tokenInfo.token.address, allowance || 0n],
        });

        // Storage the tx hash in local storage

        if (tx) {
          storedTx = await StorageUtils.storeTx({
            hash: tx.hash,
            functionName,
            labels: {
              executing: 'Resetting Collateral...',
              success: 'Success!',
              error: 'Error!',
            },
            value: args.amount,
            ...args,
          });
        }
      } catch (err) {
        console.error(err);
        if (isUserRejectedError(err as BaseError)) {
          throw new Error((err as UserRejectedRequestError).shortMessage);
        }
        const error = isViemBaseError(err as BaseError)
          ? new Error((err as BaseError).shortMessage)
          : err;
        if (isError(error)) {
          IchiInsights.trackException({
            error,
            properties: {
              ...args,
              handledAt: `mintApi:${OneTokenMint.resetCollateralAllowanceFN}`,
            },
          });
          throw error;
        }
      }
      return storedTx;
    }

    async approveCollateralAllowance(
      args: MintArgs
    ): Promise<Nullable<StorageUtils.StoredTx>> {
      let storedTx: Nullable<StorageUtils.StoredTx> = null;

      if (!args.tokenToMint) {
        console.warn('Cannot get mint related data without a token to mint');
        const message = 'No token to mint address provided';
        const error = new Error(message);
        IchiInsights.trackException({
          error,
          properties: {
            ...args,
            handledAt: 'mintApi:approveCollateralAllowance',
          },
        });
        throw error;
      }

      const tokenInfo = MINTABLE_TOKENS[args.chainId][args.tokenToMint];

      if (!tokenInfo) {
        const message = `Unable to mint ${args.amount} Could not find contract with address: ${args.tokenToMint}`;
        const error = new Error(message);
        IchiInsights.trackException({
          error,
          properties: {
            ...args,
            handledAt: `mintApi:${OneTokenMint.mintFN}`,
          },
        });
        throw new Error(message);
      }

      if (!tokenInfo.collateralToken) {
        const message = 'No collateral contract defined!';
        const error = new Error(message);
        IchiInsights.trackException({
          error,
          properties: {
            ...args,
            handledAt: `mintApi:${OneTokenMint.approveCollateralAllowanceFN}`,
          },
        });
        throw error;
      }

      const functionName = OneTokenMint.approveCollateralAllowanceFN;

      try {
        const tx = await writeContract({
          address: tokenInfo.collateralToken.address,
          abi: erc20,
          functionName: 'approve',
          args: [tokenInfo.token.address, OneTokenMint.maxApproval],
          gas: CryptoUtils.calculateGasMargin(
            BigInt(GAS_SETTINGS.approve),
            args.chainId
          ),
        });

        // Storage the tx hash in local storage

        if (tx) {
          storedTx = await StorageUtils.storeTx({
            hash: tx.hash,
            functionName,
            labels: {
              executing: 'Approving Collateral...',
              success: 'Success!',
              error: 'Error!',
            },
            value: args.amount,
            ...args,
          });
        }
      } catch (err) {
        console.error(err);
        if (isUserRejectedError(err as BaseError)) {
          throw new Error((err as UserRejectedRequestError).shortMessage);
        }
        const error = isViemBaseError(err as BaseError)
          ? new Error((err as BaseError).shortMessage)
          : err;

        IchiInsights.trackException({
          error: error as Error,
          properties: {
            ...args,
            handledAt: `mintApi:${OneTokenMint.approveCollateralAllowanceFN}`,
          },
        });
      }
      return storedTx;
    }

    async getMintData(args: MintArgs): Promise<GetMintDataResponse> {
      if (!args.tokenToMint) {
        console.warn('Cannot get mint related data without a token to mint');
        const message = 'No token to mint address provided';
        const error = new Error(message);
        IchiInsights.trackException({
          error,
          properties: {
            ...args,
            handledAt: 'mintApi:getMintData',
          },
        });
        throw error;
      }

      const tokenInfo = MINTABLE_TOKENS[args.chainId][args.tokenToMint];

      if (!tokenInfo) {
        console.warn('Cannot get mint related data without a contract defined');
        const message = 'No token contract container provided';
        const error = new Error(message);
        IchiInsights.trackException({
          error,
          properties: {
            ...args,
            handledAt: 'mintApi:getMintData',
          },
        });
        throw error;
      }

      if (!tokenInfo.memberToken) {
        console.warn(
          'Cannot get mint related data without a member contract defined (Need to connect a wallet)'
        );
        const message = 'No member contract defined!';
        const error = new Error(message);
        IchiInsights.trackException({
          error,
          properties: {
            ...args,
            handledAt: 'mintApi:getMintData',
          },
        });
        throw error;
      }
      if (!tokenInfo.collateralToken) {
        console.warn(
          'Cannot get mint related data without a collateral contract defined (Need to connect a wallet)'
        );
        const message = 'No collateral contract defined!';
        const error = new Error(message);
        IchiInsights.trackException({
          error,
          properties: {
            ...args,
            handledAt: 'mintApi:getMintData',
          },
        });
        throw error;
      }
      let memberTokenString = '0';
      let memberTokenBN = 0n;
      let collateralTokenBN = 0n;
      let isSufficientCollateral = false;
      let isSufficientMemberToken = false;
      let isMemberApproved = false;
      let isCollateralApproved = false;

      const amount = args.amount || '0';
      if (amount) {
        try {
          if (
            isOneTokenV1Contract(tokenInfo.token, {
              chainId: args.chainId,
              contractContainer: tokenInfo,
            })
          ) {
            const { collateralUSDValue, memberTokensUSDValue } =
              MintApiUtils.calculateCollateralAndMemberUsdValues(
                amount,
                args.mintingRatio,
                args.mintingFee
              );
            let memberTokensReq = '0';
            if (memberTokensUSDValue === 0n) {
              isMemberApproved = true;
              isSufficientMemberToken = true;
            } else {
              const [memberOracle] = await readContract({
                address: tokenInfo.token.address,
                abi: tokenInfo.abi,
                functionName: 'assets',
                args: [tokenInfo.memberToken.address],
                chainId: args.chainId,
              });
              if (memberOracle) {
                // const strAmount = NumberUtils.getBNString18(memberTokensUSDValue);
                const res = await readContract({
                  address: memberOracle,
                  abi: commonOracle,
                  functionName: 'amountRequired',
                  args: [tokenInfo.memberToken.address, memberTokensUSDValue],
                  chainId: args.chainId,
                });
                memberTokensReq = res[0].toString();
              }

              memberTokenString = memberTokensReq;

              // Check if member token is approved and there is enough of it
              if (tokenInfo.memberToken) {
                memberTokenBN = memberTokensUSDValue;

                const memberAllowance = args.accountAddress
                  ? await readContract({
                      address: tokenInfo.memberToken.address,
                      abi: erc20,
                      functionName: 'allowance',
                      args: [args.accountAddress, tokenInfo.token.address],
                      chainId: args.chainId,
                    })
                  : 0n;

                isMemberApproved = MintApiUtils.isMemberApproved(
                  memberAllowance,
                  memberTokensReq
                );

                const memberTokenBalance = args.accountAddress
                  ? await readContract({
                      address: tokenInfo.memberToken.address,
                      abi: erc20,
                      functionName: 'balanceOf',
                      args: [args.accountAddress],
                      chainId: args.chainId,
                    })
                  : 0n;

                isSufficientMemberToken = MintApiUtils.isSufficientMemberToken(
                  memberTokenBalance,
                  memberTokensReq
                );
              }
            }

            // Check if collateral token is approved and there is enough of it
            if (tokenInfo.collateralToken) {
              collateralTokenBN = collateralUSDValue;

              const collateralAllowance = args.accountAddress
                ? await readContract({
                    address: tokenInfo.collateralToken.address,
                    abi: erc20,
                    functionName: 'allowance',
                    args: [args.accountAddress, tokenInfo.token.address],
                    chainId: args.chainId,
                  })
                : 0n;

              isCollateralApproved = MintApiUtils.isCollateralApproved(
                collateralAllowance,
                tokenInfo.collateralToken.decimals,
                collateralUSDValue
              );

              const collateralTokenBalance = args.accountAddress
                ? await readContract({
                    address: tokenInfo.collateralToken.address,
                    abi: erc20,
                    functionName: 'balanceOf',
                    args: [args.accountAddress],
                    chainId: args.chainId,
                  })
                : 0n;

              isSufficientCollateral = MintApiUtils.isSufficientCollateral(
                collateralTokenBalance,
                tokenInfo.collateralToken.decimals,
                collateralUSDValue
              );
            }
          } else {
            throw new Error(
              `Could not narrow contract type: ${tokenInfo.token.symbol}`
            );
          }
        } catch (error) {
          if (isError(error)) {
            IchiInsights.trackException({
              error,
              properties: {
                ...args,
                handledAt: 'mintApi:getMintData',
              },
            });
            console.error(
              `Could not get minting values for: ${tokenInfo.token.symbol}`,
              error
            );
          }
        }
      }

      const result: GetMintDataResponse = {
        memberTokenString,
        memberTokenBN,
        collateralTokenBN,
        isSufficientCollateral,
        isSufficientMemberToken,
        isMemberApproved,
        isCollateralApproved,
      };
      console.debug('[mintApi.getMintData]', result);
      return result;
    }
  }
}
